/**
 * @component CompanyAboutPage
 * @description 회사소개 페이지 — 회사소개.html 프로토타입과 동일한 구조로 구성됩니다.
 *   Hero3D → ScrollNav → 미션/비전 → 핵심역량 → 규정준수 → 연혁 → 조직도 → 오시는길 → CTA
 *   오시는 길 섹션은 기존 카카오맵 API를 유지합니다.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import OrganizationChart from '../../components/company/OrganizationChart';
import LocationSection from '../../components/company/LocationSection';
import { missionVisionCards, capabilityCards, complianceBadges } from '../../constants/companyConstants';
import { timeline } from '../../constants/companyData';
import { company1 as heroImage } from '../../assets';

// 2010년 이후 연혁만 회사소개 페이지에 표시 (전체 연혁은 연혁 페이지 참조)
const HISTORY_CUTOFF_YEAR = 2010;
const timelineAbridged = timeline.filter(
  (item) => parseInt(item.year, 10) >= HISTORY_CUTOFF_YEAR,
);

/* ── 프로토타입과 동일한 섹션 ID 목록 ── */
const SCROLL_NAV_SECTIONS = [
  { id: 'sec-intro',      label: '회사 소개' },
  { id: 'sec-capability', label: '핵심 역량' },
  { id: 'sec-compliance', label: '인증·규정' },
  { id: 'sec-history',    label: '연혁' },
  { id: 'sec-org',        label: '조직도' },
  { id: 'sec-location',   label: '오시는 길' },
];


/* ── ScrollNav: 현재 뷰포트 섹션을 감지해 우측 도트 네비게이션 표시 ── */
function ScrollNav() {
  const navRef = useRef(null);
  const [active, setActive] = useState(SCROLL_NAV_SECTIONS[0].id);
  /* 실제 hero3d 영역과 겹치는지 측정된 경우에만 밝은 색상으로 전환 */
  const [isOverHero, setIsOverHero] = useState(false);

  useEffect(() => {
    let frameId = 0;

    const updateActive = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const activationLine = window.innerHeight * 0.42;
        const current = SCROLL_NAV_SECTIONS.reduce((matchedId, { id }) => {
          const el = document.getElementById(id);
          if (!el) return matchedId;
          return el.getBoundingClientRect().top <= activationLine ? id : matchedId;
        }, SCROLL_NAV_SECTIONS[0].id);

        setActive((prev) => (prev === current ? prev : current));
      });
    };

    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);
    window.addEventListener('load', updateActive);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
      window.removeEventListener('load', updateActive);
    };
  }, []);

  useLayoutEffect(() => {
    const hero = document.querySelector('.hero3d');
    const nav = navRef.current;
    if (!hero || !nav) {
      setIsOverHero(false);
      return undefined;
    }

    let frameId = 0;

    const update = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        /* scroll-nav 전체가 hero3d 안에 있을 때만 흰색입니다.
           nav 마지막 항목이 hero 밖/sec-intro 쪽으로 1px이라도 넘어가면 검정색으로 전환합니다. */
        const heroRect = hero.getBoundingClientRect();
        const navRect = nav.getBoundingClientRect();
        const isFullyInsideHero =
          navRect.top >= heroRect.top && navRect.bottom <= heroRect.bottom;

        setIsOverHero((prev) => (prev === isFullyInsideHero ? prev : isFullyInsideHero));
      });
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('load', update);
    };
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };

  return (
    <nav
      ref={navRef}
      className={`scroll-nav${isOverHero ? ' is-over-hero' : ''}`}
      aria-label="section navigation"
    >
      <ul>
        {SCROLL_NAV_SECTIONS.map(({ id, label }) => (
          <li key={id} className={active === id ? 'is-active' : ''}>
            <button onClick={() => goTo(id)} aria-current={active === id ? 'true' : undefined}>
              <span className="sn-label">{label}</span>
              <span className="sn-marker" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ── Hero3D: 배경 이미지 + 애니메이션 타이틀 ── */
function Hero3D() {
  return (
    <section className="hero3d" aria-label="회사소개 히어로">
      <div className="hero3d-bg" style={{ '--hero3d-bg-image': `url(${heroImage})` }} />
      <div className="hero3d-vignette" />

      <div className="hero3d-content">
        <div className="hero3d-eyebrow">
          <span className="hero3d-dot" />
          <span>COMPANY</span>
          <span className="hero3d-divider" />
          <span className="hero3d-eyebrow-sub">새힘정보기술 · Saehim IT</span>
        </div>
        <h1 className="hero3d-title">
          제약·화장품 제조 현장을 이해하는<br />
          <em>IT 파트너</em>
        </h1>
        <p className="hero3d-sub">
          새힘정보기술은 RWS, MES, WMS를 기반으로<br />
          칭량·제조·창고·유지보수까지 이어지는 제조 운영 시스템을 구축합니다.
        </p>
      </div>

      <div className="hero3d-scroll" aria-hidden="true">
        <span>SCROLL</span>
        <div className="hero3d-scroll-line" />
      </div>
    </section>
  );
}

export default function CompanyAboutPage({ onNavigate }) {
  /* fade-up: IntersectionObserver로 .fade-up 요소를 뷰포트 진입 시 is-visible 추가 */
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );
    document.querySelectorAll('.company-about .fade-up').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="company-about">
      {/* ── 히어로 ── */}
      <Hero3D />

      {/* ── 우측 스크롤 네비게이션 ── */}
      <ScrollNav />

      {/* ── 1. 미션 / 비전 / 핵심 가치 ── */}
      <div id="sec-intro">
        <section className="ab-section company-about">
          <div className="container">
            <div className="section-head fade-up">
              <span className="section-eyebrow">Mission · Vision · Value</span>
              <h2 className="section-title">
                제약·화장품 제조 현장을 이해하는{' '}
                <span style={{ color: 'var(--color-primary)' }}>IT 파트너</span>
              </h2>
              <p className="section-desc">
                새힘정보기술은 RWS, MES, WMS를 기반으로 칭량·제조·창고·유지보수까지 이어지는 제조 운영 시스템을 구축합니다.
              </p>
            </div>
            <div className="mvc-grid fade-up">
              {missionVisionCards.map(({ Icon, iconClass, no, title, heading, text }, i) => (
                <div key={title} className="mvc-card" data-tone={iconClass}>
                  <div className="mvc-card-head">
                    <div className="mvc-icon">
                      <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
                    </div>
                    <span className="mvc-no">0{i + 1} / 03</span>
                  </div>
                  <span className="mvc-tag">{title}</span>
                  <h3 className="mvc-heading">{heading}</h3>
                  <p className="mvc-text">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── 2. 핵심 역량 ── */}
      <div id="sec-capability">
        <section className="ab-section ab-section-alt company-about">
          <div className="container">
            <div className="section-head fade-up">
              <span className="section-eyebrow">Core Capability</span>
              <h2 className="section-title">시스템이 아니라, 운영을 만듭니다.</h2>
              <p className="section-desc">
                제약·화장품 산업의 제조 현장을 정확하게 이해하고,
                구축에서 운영까지 끊김 없는 한 흐름으로 연결합니다.
              </p>
            </div>
            <div className="cap-grid fade-up">
              {capabilityCards.map((item, i) => (
                <article key={item.title} className="cap-card">
                  <div>
                    <div className="cap-meta">
                      <span className="cap-no">0{i + 1}</span>
                      <span className="cap-tag">{item.tag}</span>
                    </div>
                    <h3 className="cap-title">{item.title}</h3>
                    <p className="cap-text">{item.text}</p>
                  </div>
                  <div className="cap-kpi">
                    <div className="cap-kpi-num">{item.kpiNum}</div>
                    <div className="cap-kpi-label">{item.kpiLabel}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ── 3. 인증 및 규정 준수 ── */}
      <div id="sec-compliance">
        <section className="ab-section ab-section-tight company-about">
          <div className="container">
            <div className="compliance-strip fade-up">
              <div>
                <div className="section-eyebrow">Compliance</div>
                <h3>제약·화장품 GMP 기준에 맞춘<br />시스템 검증 이력을 제공합니다.</h3>
                <p>식약처 기준의 CSV, 전자서명, 변경관리 요건을 반영합니다.</p>
              </div>
              <div className="compliance-badges" role="list" aria-label="인증 및 규정 준수 항목">
                {complianceBadges.map((badge) => (
                  <span key={badge} className="compliance-badge" role="listitem">
                    <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── 4. 연혁 타임라인 ── */}
      <div id="sec-history">
        <section className="ab-section company-about">
          <div className="container">
            <div className="timeline">
              <aside className="timeline-side fade-up">
                <span className="section-eyebrow">History</span>
                <h2>20년의 누적된<br />제조 운영 경험</h2>
                <p>2004년 설립 이후, 제약·화장품 제조 현장에서 시스템 구축과 운영을 이어오고 있습니다. 주요 마일스톤을 확인하세요.</p>
              </aside>
              <div className="fade-up" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div className="timeline-list">
                  {timelineAbridged.map((item, i) => (
                    <div
                      key={i}
                      className={`timeline-item${item.highlight ? ' is-highlight' : ''}`}
                    >
                      <div className="timeline-year">{item.year}</div>
                      <div className="timeline-text">{item.text}</div>
                    </div>
                  ))}
                  {/* 2010년 이전 생략 표시 */}
                  <div className="timeline-item timeline-ellipsis">
                    <div className="timeline-year" />
                    <div className="timeline-text" style={{ color: '#aaa', letterSpacing: '0.1em' }}>· · ·</div>
                  </div>
                </div>
                {/* 전체 연혁 페이지 링크 — 타임라인 오른쪽 끝 정렬 */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <button
                    className="timeline-more-link"
                    onClick={() => onNavigate('/company/history.html')}
                  >
                    더 많은 연혁 &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ── 5. 조직도 ── */}
      <div id="sec-org">
        <section className="ab-section ab-section-alt company-about">
          <div className="container">
            <div className="section-head fade-up">
              <span className="section-eyebrow">Organization</span>
              <h2 className="section-title">현장과 운영, 기술을 잇는 조직</h2>
              <p className="section-desc">
                SI 부문이 시스템을 구축하고, SM 부문이 운영을 책임지며,
                AX 추진팀이 전사 디지털 전환을 끌어갑니다.
              </p>
            </div>
            <div className="fade-up">
              <OrganizationChart />
            </div>
          </div>
        </section>
      </div>

      {/* ── 6. 오시는 길 — 카카오맵 API 유지 ── */}
      <div id="sec-location">
        <section className="ab-section company-about">
          <div className="container">
            <div className="section-head fade-up">
              <span className="section-eyebrow">Office Location</span>
              <h2 className="section-title">오시는 길</h2>
              <p className="section-desc location-section-desc">
                본사(충북 청주)와 안양지사(경기 안양) 두 곳에서 고객을 맞이합니다.
                방문 상담은 사전 문의 및 일정 조율을 통해 진행합니다.
              </p>
            </div>
            <div className="fade-up">
              <LocationSection />
            </div>
          </div>
        </section>
      </div>

    </div>
  );
}
